import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Plus, Edit, Trash2, Globe, Pause, Play, Archive, Undo2 } from 'lucide-react';
import { domainsApi, companiesApi, customersApi } from '../services/api';
import { useTableSort } from '../hooks/useTableSort';
import { useToast } from '../hooks/use-toast';

const emptyForm = () => ({
  domainName: '', domainEnding: '', wert: '', currentLocation: '', targetLocation: '',
  websiteExists: '', websiteLocation: '', costNetPerYear: '', resellerLevelC: '',
  comment: '', companyId: '', customerId: '', status: 'active',
  expiryDate: '', reseller: '', ownerAccount: '',
});

const DomainManager = () => {
  const { toast } = useToast();
  const [domains, setDomains] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [search, setSearch] = useState('');
  const [brandFilter, setBrandFilter] = useState('all');
  const [registrarFilter, setRegistrarFilter] = useState('all');
  const [viewTab, setViewTab] = useState('aktiv');

  useEffect(() => {
    load();
    companiesApi.getAll().then(r => setCompanies(r.data || [])).catch(() => {});
    customersApi.getAll('', 500).then(r => setCustomers(r.data || [])).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await domainsApi.getAll();
      setDomains(res.data || []);
    } catch (e) {
      toast({ title: 'Fehler', description: 'Domains konnten nicht geladen werden.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const brandName = (id) => companies.find(c => c.id === id)?.name || '—';
  const customerLabel = (id) => { const c = customers.find(x => x.id === id); return c ? (c.company || c.name) : ''; };
  const registrars = Array.from(new Set(domains.map(d => d.currentLocation).filter(Boolean))).sort();

  const tabOf = (d) => d.status === 'paused' ? 'pausiert' : d.status === 'archived' ? 'archiv' : 'aktiv';

  const q = search.trim().toLowerCase();
  const filtered = domains
    .filter(d => {
      if (tabOf(d) !== viewTab) return false;
      if (brandFilter !== 'all' && d.companyId !== brandFilter) return false;
      if (registrarFilter !== 'all' && d.currentLocation !== registrarFilter) return false;
      if (q && !(`${d.domainName || ''} ${d.currentLocation || ''} ${d.targetLocation || ''} ${d.websiteLocation || ''} ${d.comment || ''} ${customerLabel(d.customerId)} ${brandName(d.companyId)}`.toLowerCase().includes(q))) return false;
      return true;
    })
    .map(d => ({ ...d, _brand: brandName(d.companyId), _customer: customerLabel(d.customerId) }));

  const { sorted, requestSort, indicator } = useTableSort(filtered, 'domainName');

  const yearlySum = filtered.reduce((s, d) => s + (Number(d.costNetPerYear) || 0), 0);
  const resellerSum = filtered.reduce((s, d) => s + (Number(d.resellerLevelC) || 0), 0);

  const openCreate = () => { setEditing(null); setForm(emptyForm()); setShowForm(true); };
  const openEdit = (d) => {
    setEditing(d);
    setForm({
      domainName: d.domainName || '', domainEnding: d.domainEnding || '', wert: d.wert ?? '',
      currentLocation: d.currentLocation || '', targetLocation: d.targetLocation || '',
      websiteExists: d.websiteExists || '', websiteLocation: d.websiteLocation || '',
      costNetPerYear: d.costNetPerYear ?? '', resellerLevelC: d.resellerLevelC ?? '',
      comment: d.comment || '', companyId: d.companyId || '', customerId: d.customerId || '',
      status: d.status || 'active',
      expiryDate: (d.expiryDate || '').slice(0, 10), reseller: d.reseller || '', ownerAccount: d.ownerAccount || '',
    });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.domainName.trim()) { toast({ title: 'Domain fehlt', description: 'Bitte den Domainnamen eingeben.', variant: 'destructive' }); return; }
    const num = (v) => (v === '' || v === null || v === undefined) ? null : parseFloat(v);
    const payload = {
      domainName: form.domainName.trim(),
      domainEnding: form.domainEnding.trim() || null,
      wert: form.wert === '' ? null : String(form.wert),
      currentLocation: form.currentLocation.trim() || null,
      targetLocation: form.targetLocation.trim() || null,
      websiteExists: form.websiteExists.trim() || null,
      websiteLocation: form.websiteLocation.trim() || null,
      costNetPerYear: num(form.costNetPerYear),
      resellerLevelC: num(form.resellerLevelC),
      comment: form.comment.trim() || null,
      companyId: form.companyId || null,
      customerId: form.customerId || null,
      customerName: form.customerId ? customerLabel(form.customerId) : null,
      status: form.status,
      expiryDate: form.expiryDate || null,
      reseller: form.reseller.trim() || null,
      ownerAccount: form.ownerAccount.trim() || null,
    };
    try {
      if (editing) await domainsApi.update(editing.id, payload);
      else await domainsApi.create(payload);
      toast({ title: editing ? 'Domain aktualisiert' : 'Domain angelegt', description: `${payload.domainName} gespeichert.` });
      setShowForm(false);
      load();
    } catch (e) {
      toast({ title: 'Fehler', description: 'Domain konnte nicht gespeichert werden.', variant: 'destructive' });
    }
  };

  const quickStatus = async (d, status) => {
    try { await domainsApi.update(d.id, { status }); load(); }
    catch (e) { toast({ title: 'Fehler', description: 'Status konnte nicht geändert werden.', variant: 'destructive' }); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Domain "${d.domainName}" wirklich löschen?`)) return;
    try { await domainsApi.delete(d.id); toast({ title: 'Domain gelöscht' }); load(); }
    catch (e) { toast({ title: 'Fehler', description: 'Domain konnte nicht gelöscht werden.', variant: 'destructive' }); }
  };

  const today = new Date();
  const daysUntil = (iso) => {
    if (!iso) return null;
    const dt = new Date(iso);
    if (isNaN(dt)) return null;
    return Math.ceil((dt - today) / 86400000);
  };
  const soonCount = filtered.filter(d => { const n = daysUntil(d.expiryDate); return n !== null && n <= 30 && d.status === 'active'; }).length;
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('de-DE') : '—';
  const expiryCell = (d) => {
    const n = daysUntil(d.expiryDate);
    if (n === null) return <span className="text-slate-400">—</span>;
    if (n < 0) return <Badge className="bg-red-100 text-red-700">abgelaufen</Badge>;
    if (n <= 30) return <Badge className="bg-amber-100 text-amber-700">in {n} T · {fmtDate(d.expiryDate)}</Badge>;
    return <span className="text-slate-600 whitespace-nowrap">{fmtDate(d.expiryDate)}</span>;
  };
  const fmtEur = (n) => (n === null || n === undefined || n === '') ? '—' : (Number(n) || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

  const th = (label, key, extra = '') => (
    <TableHead className={`cursor-pointer select-none hover:bg-slate-50 whitespace-nowrap ${extra}`} onClick={() => requestSort(key)}>
      {label}{indicator(key)}
    </TableHead>
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <Button onClick={openCreate} className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white">
          <Plus className="w-4 h-4" /> Neue Domain
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card><CardContent className="p-4 text-center">
          <div className="text-2xl font-mono font-bold tracking-tight text-slate-800">{filtered.length}</div>
          <div className="text-sm text-slate-600">Domains (angezeigt)</div>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <div className="text-2xl font-mono font-bold tracking-tight text-slate-800">{fmtEur(yearlySum)}</div>
          <div className="text-sm text-slate-600">Kosten / Jahr (netto)</div>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <div className={`text-2xl font-mono font-bold tracking-tight ${soonCount ? 'text-amber-600' : 'text-slate-800'}`}>{soonCount}</div>
          <div className="text-sm text-slate-600">Läuft in ≤ 30 Tagen ab</div>
        </CardContent></Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle className="flex items-center gap-2"><Globe className="w-5 h-5" /> Domain-Register</CardTitle>
            <div className="flex flex-wrap gap-2 items-center">
              <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
                {[['aktiv', 'Aktiv'], ['pausiert', 'Pausiert'], ['archiv', 'Archiv']].map(([key, label]) => (
                  <button key={key} type="button" onClick={() => setViewTab(key)}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${viewTab === key ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}>
                    {label} ({domains.filter(d => tabOf(d) === key).length})
                  </button>
                ))}
              </div>
              <Input placeholder="Suchen (Domain, Registrar, Kunde, Kommentar)…" value={search} onChange={(e) => setSearch(e.target.value)} className="w-64" />
              <Select value={brandFilter} onValueChange={setBrandFilter}>
                <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Brands</SelectItem>
                  {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={registrarFilter} onValueChange={setRegistrarFilter}>
                <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Registrare</SelectItem>
                  {registrars.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading && domains.length === 0 ? (
            <div className="text-center py-8 text-slate-500">Lade Domains…</div>
          ) : sorted.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Globe className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>{domains.length === 0 ? 'Noch keine Domains angelegt' : 'Keine Treffer für die aktuelle Filterung'}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {th('Domain', 'domainName')}
                    {th('Endung', 'domainEnding')}
                    {th('Wert', 'wert')}
                    {th('Ablauf', 'expiryDate')}
                    {th('Liegt bei', 'currentLocation')}
                    {th('Soll zu', 'targetLocation')}
                    {th('Website?', 'websiteExists')}
                    {th('Website-Standort', 'websiteLocation')}
                    {th('Kosten/Jahr', 'costNetPerYear', 'text-right')}
                    {th('Reseller C', 'resellerLevelC', 'text-right')}
                    {th('Brand', '_brand')}
                    {th('Kunde', '_customer')}
                    <TableHead className="whitespace-nowrap">Kommentar</TableHead>
                    <TableHead className="text-right">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sorted.map(d => (
                    <TableRow key={d.id}>
                      <TableCell className="whitespace-nowrap">
                        <button type="button" onClick={() => openEdit(d)} title="Bearbeiten"
                          className="font-medium text-slate-800 hover:text-amber-700 hover:underline text-left">
                          {d.domainName}
                        </button>
                      </TableCell>
                      <TableCell>{d.domainEnding || '—'}</TableCell>
                      <TableCell>{d.wert || '—'}</TableCell>
                      <TableCell>{expiryCell(d)}</TableCell>
                      <TableCell className="whitespace-nowrap">{d.currentLocation || '—'}</TableCell>
                      <TableCell className="whitespace-nowrap">{d.targetLocation || '—'}</TableCell>
                      <TableCell>{d.websiteExists || '—'}</TableCell>
                      <TableCell className="whitespace-nowrap">{d.websiteLocation || '—'}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">{fmtEur(d.costNetPerYear)}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">{fmtEur(d.resellerLevelC)}</TableCell>
                      <TableCell><Badge variant="secondary" className="bg-slate-100 text-slate-600 font-normal whitespace-nowrap">{d._brand}</Badge></TableCell>
                      <TableCell className="whitespace-nowrap">{d._customer || <span className="text-slate-400">—</span>}</TableCell>
                      <TableCell className="max-w-[220px] truncate" title={d.comment || ''}>{d.comment || '—'}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {d.status === 'active' && <Button size="sm" variant="outline" title="Pausieren" onClick={() => quickStatus(d, 'paused')}><Pause className="w-3.5 h-3.5" /></Button>}
                          {d.status === 'paused' && <Button size="sm" variant="outline" title="Fortsetzen" onClick={() => quickStatus(d, 'active')}><Play className="w-3.5 h-3.5" /></Button>}
                          {tabOf(d) !== 'archiv'
                            ? <Button size="sm" variant="outline" title="Archivieren" onClick={() => quickStatus(d, 'archived')}><Archive className="w-3.5 h-3.5" /></Button>
                            : <Button size="sm" variant="outline" title="Reaktivieren" onClick={() => quickStatus(d, 'active')}><Undo2 className="w-3.5 h-3.5" /></Button>}
                          <Button size="sm" variant="outline" onClick={() => openEdit(d)}><Edit className="w-3.5 h-3.5" /></Button>
                          <Button size="sm" variant="outline" className="text-red-600" onClick={() => remove(d)}><Trash2 className="w-3.5 h-3.5" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? 'Domain bearbeiten' : 'Neue Domain'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <Label>Domainname *</Label>
                <Input value={form.domainName} onChange={(e) => setForm({ ...form, domainName: e.target.value })} placeholder="beispiel.de" />
              </div>
              <div>
                <Label>Endung</Label>
                <Input value={form.domainEnding} onChange={(e) => setForm({ ...form, domainEnding: e.target.value })} placeholder="de" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Wert</Label>
                <Input value={form.wert} onChange={(e) => setForm({ ...form, wert: e.target.value })} />
              </div>
              <div>
                <Label>Liegt bei</Label>
                <Input value={form.currentLocation} onChange={(e) => setForm({ ...form, currentLocation: e.target.value })} placeholder="Netcup, Strato…" />
              </div>
              <div>
                <Label>Soll liegen bei</Label>
                <Input value={form.targetLocation} onChange={(e) => setForm({ ...form, targetLocation: e.target.value })} placeholder="Netcup" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Website vorhanden?</Label>
                <Input value={form.websiteExists} onChange={(e) => setForm({ ...form, websiteExists: e.target.value })} placeholder="JA / NEIN" />
              </div>
              <div>
                <Label>Wo liegt die Website</Label>
                <Input value={form.websiteLocation} onChange={(e) => setForm({ ...form, websiteLocation: e.target.value })} placeholder="Plesk, Raidboxes…" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Kosten netto / Jahr</Label>
                <Input type="number" step="0.01" value={form.costNetPerYear} onChange={(e) => setForm({ ...form, costNetPerYear: e.target.value })} placeholder="3.24" />
              </div>
              <div>
                <Label>Reseller Level C</Label>
                <Input type="number" step="0.01" value={form.resellerLevelC} onChange={(e) => setForm({ ...form, resellerLevelC: e.target.value })} placeholder="1.92" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Brand (verwaltende Firma)</Label>
                <Select value={form.companyId || 'none'} onValueChange={(v) => setForm({ ...form, companyId: v === 'none' ? '' : v })}>
                  <SelectTrigger><SelectValue placeholder="Keine" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— keine —</SelectItem>
                    {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Kunde (optional)</Label>
                <Select value={form.customerId || 'none'} onValueChange={(v) => setForm({ ...form, customerId: v === 'none' ? '' : v })}>
                  <SelectTrigger><SelectValue placeholder="Kein Kunde" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— kein Kunde —</SelectItem>
                    {customers.map(c => <SelectItem key={c.id} value={c.id}>{c.company || c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Ablaufdatum</Label>
                <Input type="date" value={form.expiryDate} onChange={(e) => setForm({ ...form, expiryDate: e.target.value })} />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Aktiv</SelectItem>
                    <SelectItem value="paused">Pausiert</SelectItem>
                    <SelectItem value="archived">Gekündigt / Archiviert</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Reseller</Label>
                <Input value={form.reseller} onChange={(e) => setForm({ ...form, reseller: e.target.value })} placeholder="rsl… / unbekannt" />
              </div>
              <div>
                <Label>Inhaber / Account</Label>
                <Input value={form.ownerAccount} onChange={(e) => setForm({ ...form, ownerAccount: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Kommentar</Label>
              <Input value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowForm(false)}>Abbrechen</Button>
              <Button className="bg-amber-500 hover:bg-amber-600 text-white" onClick={save}>{editing ? 'Speichern' : 'Anlegen'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DomainManager;
